import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle, XCircle, AlertCircle, X } from 'lucide-react';
import { cn } from '../../utils/cn';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
}

// Global toast state and functions
let toasts: Toast[] = [];
let setToastsState: React.Dispatch<React.SetStateAction<Toast[]>> | null = null;

// Toast deduplication - track recent messages to prevent spam
interface ToastRecord {
  message: string;
  type: ToastType;
  timestamp: number;
}
const recentToasts: Map<string, ToastRecord> = new Map();
const DEDUP_WINDOW_MS = 5000; // 5 seconds - don't show same message within this window

// Clean up old toast records periodically
setInterval(() => {
  const now = Date.now();
  for (const [key, record] of recentToasts.entries()) {
    if (now - record.timestamp > DEDUP_WINDOW_MS) {
      recentToasts.delete(key);
    }
  }
}, 10000); // Clean up every 10 seconds

export const toast = {
  success: (message: string, options?: { duration?: number }) => {
    addToast({ message, type: 'success', duration: options?.duration });
  },
  error: (message: string, options?: { duration?: number }) => {
    addToast({ message, type: 'error', duration: options?.duration });
  },
  info: (message: string, options?: { duration?: number }) => {
    addToast({ message, type: 'info', duration: options?.duration });
  },
  warning: (message: string, options?: { duration?: number }) => {
    addToast({ message, type: 'warning', duration: options?.duration });
  },
};

const addToast = ({ message, type, duration }: { message: string; type: ToastType; duration?: number }) => {
  // Check for duplicate toast within dedup window
  const deduplicationKey = `${type}:${message}`;
  const existingToast = recentToasts.get(deduplicationKey);

  if (existingToast) {
    const timeSinceLastToast = Date.now() - existingToast.timestamp;
    if (timeSinceLastToast < DEDUP_WINDOW_MS) {
      console.log(`🚫 Toast deduplication: Skipping duplicate "${message}" (${timeSinceLastToast}ms since last)`);
      return; // Skip duplicate toast
    }
  }

  // Record this toast for deduplication
  recentToasts.set(deduplicationKey, {
    message,
    type,
    timestamp: Date.now()
  });

  const id = Math.random().toString(36).substring(2, 9);
  const newToast = { id, message, type, duration };

  if (setToastsState) {
    setToastsState((prev) => [...prev, newToast]);

    // Auto-remove after specified duration or default
    // Error messages get 10 seconds by default, others get 4 seconds
    const defaultDuration = type === 'error' ? 10000 : 4000;
    const timeoutDuration = duration !== undefined ? duration : defaultDuration;

    setTimeout(() => {
      removeToast(id);
    }, timeoutDuration);
  }
};

const removeToast = (id: string) => {
  if (setToastsState) {
    setToastsState((prev) => prev.filter((toast) => toast.id !== id));
  }
};

export const Toaster: React.FC = () => {
  const [localToasts, setLocalToasts] = useState<Toast[]>([]);
  
  // Set the state updater function on mount
  useEffect(() => {
    setToastsState = setLocalToasts;
    
    return () => {
      setToastsState = null;
    };
  }, []);
  
  // Update toasts reference when local state changes
  useEffect(() => {
    toasts = localToasts;
  }, [localToasts]);
  
  const toastIcon = (type: ToastType) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-success" />;
      case 'error':
        return <XCircle className="h-5 w-5 text-destructive" />;
      case 'info':
        return <AlertCircle className="h-5 w-5 text-primary" />;
      case 'warning':
        return <AlertCircle className="h-5 w-5 text-warning" />;
    }
  };
  
  return createPortal(
    <div className="fixed bottom-0 right-0 z-50 p-4 space-y-4 w-full sm:max-w-sm">
      {localToasts.map((toast) => (
        <div
          key={toast.id}
          className={cn(
            'flex items-center justify-between p-4 rounded-lg shadow-lg animate-slide-up',
            {
              'bg-success/10 border border-success/30': toast.type === 'success',
              'bg-destructive/10 border border-destructive/30': toast.type === 'error',
              'bg-primary/10 border border-primary/30': toast.type === 'info',
              'bg-warning/10 border border-warning/30': toast.type === 'warning',
            }
          )}
        >
          <div className="flex items-center space-x-3 flex-1">
            {toastIcon(toast.type)}
            <div className="text-sm font-medium whitespace-pre-line">{toast.message}</div>
          </div>
          <button
            onClick={() => removeToast(toast.id)}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}
    </div>,
    document.body
  );
};